import { Form, FormProps } from "@/components/form";
import { CollapsibleSection } from "@/components/form/collapsible-section";
import { ConfirmDialog, useOpenModal } from "@/components/modal";
import { Grid2 as Grid, Stack } from "@mui/material";
import { z } from "zod/v4";
import { useState, useRef } from "react";
import DEArticuloSearch, { DetalleDE } from "../documento-electronico-form/de-articulo-search";
import DEArticuloList from "../documento-electronico-form/de-articulo-list";
import DocumentoAsociadoFields from "../documento-electronico-form/documento-asociado-fields";
import NotaRemisionGeneralInfoFields from "./nota-remision-general-info-fields";
import TransporteFields from "./transporte-fields";
import { notaRemisionFormSchema } from "../../schemas";
import { Cliente } from "@/shared/types/cliente";

export { notaRemisionFormSchema };

interface NotaRemisionFormProps extends FormProps<z.input<typeof notaRemisionFormSchema>> {
    onClienteChange?: (cliente: Cliente | null) => void;
    cliente?: Cliente | null;
}

export function NotaRemisionForm({
    form,
    onClienteChange,
    cliente,
    ...props
}: NotaRemisionFormProps) {
    const openModal = useOpenModal();
    const detalleFormRef = useRef<any>(null);
    const [showClienteError, setShowClienteError] = useState(false);

    const handleFormSubmit = () => {
        if (!cliente) {
            setShowClienteError(true);
            return false;
        }
        setShowClienteError(false);
        return true;
    };

    const handleReset = () => {
        form.reset();
        onClienteChange?.(null);
        form.setFieldValue("clienteId", "", { dontUpdateMeta: true });
        form.setFieldValue("motivoEmisionNR", "1", { dontUpdateMeta: true });
        form.setFieldValue("responsableEmision", "1", { dontUpdateMeta: true });
        form.setFieldValue("kmRecorrido", 1, { dontUpdateMeta: true });
        form.setFieldValue("tipoDocumentoAsociado", "none", { dontUpdateMeta: true });
        form.setFieldValue("items", [], { dontUpdateMeta: true });
        setShowClienteError(false);
    };

    return <Form form={form} gap={2} {...props}>
        <NotaRemisionGeneralInfoFields
            onClienteChange={onClienteChange}
            cliente={cliente}
            clienteError={showClienteError}
        />
        <DocumentoAsociadoFields
            forNotaCredito={true}
            opcional={true}
            setCdcLoaded={() => {}}
        />
        <TransporteFields />
        <CollapsibleSection title="Items">
            <Grid container columns={{ xs: 1, md: 3 }} spacing={2} flex={1}>
                <Grid component={Stack} size={1}>
                    <DEArticuloSearch
                        ref={detalleFormRef}
                        forAutoFactura={true}
                        hidePrecio={true}
                        onArticuloSelect={(v: DetalleDE) => {
                            const items = form.getFieldValue("items");
                            const index = items.findIndex((i: DetalleDE) => i.descripcion === v.descripcion);
                            if (index !== -1) {
                                const previous = items[index];
                                form.replaceFieldValue("items", index, { ...previous, cantidad: previous.cantidad + v.cantidad });
                            } else {
                                form.pushFieldValue("items", {
                                    codigo: v.codigo,
                                    descripcion: v.descripcion,
                                    cantidad: v.cantidad,
                                    precio: v.precio,
                                    iva: v.iva,
                                    unidadMedida: v.unidadMedida
                                });
                            }
                        }}
                    />
                </Grid>
                <Grid component={Stack} size={2} gap={2}>
                    <form.Field name="items" mode="array">
                        {field => (
                            <DEArticuloList
                                items={(field.state.value || []) as DetalleDE[]}
                                remove={field.removeValue}
                                forAutoFactura={true}
                                hidePrecio={true}
                            />
                        )}
                    </form.Field>
                </Grid>
            </Grid>
        </CollapsibleSection>
        <Stack direction="row" justifyContent="end" gap={2}>
            <Form.Button onClick={() => openModal("confirmResetNR")}>Cancelar</Form.Button>
            <Form.SubmitButton onClick={handleFormSubmit}>Confirmar</Form.SubmitButton>
        </Stack>

        <ConfirmDialog code="confirmResetNR">
            <ConfirmDialog.Content
                title="Cancelar Nota de Remisión"
                description="¿Estás seguro que quieres cancelar la nota de remisión actual?"
                onConfirm={handleReset}
            />
        </ConfirmDialog>
    </Form>;
}
